import React, { useMemo, useEffect, useState } from 'react';
import { MentionsInput, Mention } from 'react-mentions';
import { withApollo } from 'react-apollo';
import { merge } from 'lodash';
import { MentionsInputStyle } from './style';
import MentionSuggestion from './mentionSuggestion';
import { withCurrentUser } from 'src/components/withCurrentUser';
import compose from 'recompose/compose';
import { getCommunityByIdQuery } from 'shared/graphql/queries/community/getCommunity';
import { canViewCGARoles } from '../../helpers/adminPanelControl';
import dataProvider from 'shared/rest';
const cleanSuggestionUserObject = user => {
  if (!user) {
    return null;
  } else {
    const name = user.name || `${user.firstName} ${user.lastName}`;
    return {
      id: user.id || user.userId,
      display: name,
      name,
      filterName: name ? name.toLowerCase() : '',
    };
  }
};

const sortSuggestions = (a, b, queryString) => {
  const aUsernameIndex = a.name.indexOf(queryString || '');
  const bUsernameIndex = b.name.indexOf(queryString || '');
  const aNameIndex = a.filterName.indexOf(queryString || '');
  const bNameIndex = b.filterName.indexOf(queryString || '');
  if (aNameIndex === 0) return -1;
  if (aUsernameIndex === 0) return -1;
  if (aNameIndex === 0) return -1;
  if (aUsernameIndex === 0) return -1;
  return aNameIndex - bNameIndex || aUsernameIndex - bUsernameIndex;
};

/**
 * @description filter the name string by query
 * @param {string} name name string
 * @param {string} query filter the name string by query
 * @returns
 */
const nameFilter = (name, query) => name && name.includes(query);

/**
 * @description filter the staticSuggestions from props with queryString
 * @param {object} param {staticSuggestions,queryString}
 * @param {string} staticSuggestions staticSuggestions from props
 * @param {string} queryString queryString
 */
const getStaticSuggestions = ({ staticSuggestions, queryString }) => {
  const filterForName = queryString
    ? user =>
        nameFilter(user.name, queryString) ||
        nameFilter(user.filterName, queryString)
    : user => user;
  staticSuggestions
    .map(cleanSuggestionUserObject)
    .filter(Boolean)
    .filter(filterForName)
    .sort((a, b) => sortSuggestions(a, b, queryString));
};

const isSuperAdmin = role => {
  return role && role.includes('SUPER_ADMIN');
};

const SpectrumMentionsInput = props => {
  const {
    staticSuggestions,
    client,
    dataCy,
    networkDisabled,
    hasAttachment,
    communityId,
    currentUser,
    ...restProps
  } = props;
  const { role: userRole, id: userId } = currentUser;
  const [isCGAGroup, setIsCGAGroup] = useState();
  const [isPrivate, setIsPrivate] = useState(true);
  // get information of currentCommunity
  useEffect(async () => {
    const { data } = await client.query({
      query: getCommunityByIdQuery,
      variables: {
        id: communityId,
      },
    });
    if (data && data.community) {
      setIsCGAGroup(
        data.community && data.community.tag && data.community.tag === 'CGA'
      );
      setIsPrivate(data.community.isPrivate);
    }
  }, [setIsCGAGroup, setIsPrivate]);
  const onSearch = async (queryString, callback) => {
    const suggestions =
      getStaticSuggestions({
        staticSuggestions: staticSuggestions || [],
        queryString: queryString.trim(),
      }) || [];
    if (queryString) {
      // Case private group
      if (isPrivate) {
        const { data } = await dataProvider.invoke(
          'communities',
          'getUsersByCommunityId',
          { queryString, communityId }
        );
        const result = data
          .map(cleanSuggestionUserObject)
          .filter(user => user.id !== userId);
        callback(Array.from(new Set([...suggestions, ...result])));
      } else {
        // Case CGA group & public group
        const filter = isCGAGroup
          ? { name: queryString, roleIds: canViewCGARoles }
          : { name: queryString };
        const params = {
          userId,
          page: 1,
          size: 100,
          filter,
        };
        const apiName = isSuperAdmin(userRole) ? 'admin' : 'user';
        const apiMethod = isSuperAdmin(userRole)
          ? 'fetchUserByCondition'
          : 'getAllFriends';
        const { data } = await dataProvider.invoke(apiName, apiMethod, params);
        let result = [];
        if (apiMethod === 'getAllFriends') {
          result = Object.values(data)
            .map(cleanSuggestionUserObject)
            .filter(user => user.id !== userId);
        } else {
          result = data.results
            .map(cleanSuggestionUserObject)
            .filter(user => user.id !== userId);
        }
        callback([...suggestions, ...result]);
      }
    } else {
      // case no query string
      return callback([...suggestions]);
    }
  };

  const style = useMemo(() => {
    return merge({}, props.style, MentionsInputStyle);
  }, [props.style]);
  return (
    <MentionsInput
      style={style}
      data-cy={props.dataCy}
      {...restProps}
      allowSuggestionsAboveCursor
    >
      <Mention
        trigger="@"
        data={onSearch}
        appendSpaceOnAdd={true}
        displayTransform={(...args) => {
          return `@${args[1]}`;
        }}
        renderSuggestion={(
          entry,
          search,
          highlightedDisplay,
          index,
          focused
        ) => (
          <MentionSuggestion
            entry={entry}
            highlightedDisplay={highlightedDisplay}
            focused={focused}
            search={search}
            index={index}
          />
        )}
      />
    </MentionsInput>
  );
};

export default withApollo(compose(withCurrentUser)(SpectrumMentionsInput));
